import { Component, computed, Inject, OnInit, Optional, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent, MatDialogRef,
  MatDialogTitle
} from "@angular/material/dialog";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";

import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";
import {MatFormField} from "@angular/material/form-field";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect, MatSelectChange, MatSelectModule} from "@angular/material/select";
import {DialogService} from "../../../services/dialog.service";
import {firstValueFrom, from, map, mergeMap, retry, tap, timer, toArray} from "rxjs";
import {UserService} from "../../../services/user.service";
import {AssessmentService} from "../../../services/assessment.service";

@Component({
    selector: 'app-export-user',
    imports: [
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatIcon,
    MatIconButton,
    ReactiveFormsModule,
    MatCheckbox,
    MatFormField,
    MatOption,
    MatSelect,
    MatSelectModule
],
    templateUrl: './export-user.component.html',
    styleUrl: './export-user.component.scss'
})
export class ExportUserComponent implements OnInit {
  formGroup!: FormGroup;
  defaultFormValues!: any;

  readonly assignmentStatuses = [{ value: 'Available', text: 'Available' }, { value: 'OnAssignment', text: 'On Assignment' }, { value: 'NotAvailable', text: 'Not Available' }];
  readonly selectAllControlNames = ['name', 'email', 'phone', 'positions', 'specificPositions', 'agency', 'specificAgencies', 'location', 'specificRegions', 'specificForests', 'assignmentStatus', 'specificAssignmentStatuses', 'skills', 'specificSkills'];
  readonly csvColumnControlNames = ['name', 'email', 'phone', 'positions', 'agency', 'location', 'assignmentStatus', 'skills'];

  previousSelections: {
    [controlName: string]: any[];
  } = {
      specificPositions: [],
      specificAgencies: [],
      specificRegions: [],
      specificForests: [],
      specificAssignmentStatuses: [],
      specificSkills: []
    }

  users = signal<any[]>([]);
  filteredUsers = computed<any[]>(() => this.users());
  indeterminate = signal<boolean>(false);
  userCount = computed<number>(() => this.filteredUsers()?.length);
  showCertOptions = signal<boolean>(true);

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly fb: FormBuilder,
    private readonly dialogService: DialogService,
    private readonly dialogRef: MatDialogRef<ExportUserComponent>,
    private readonly userService: UserService,
    private readonly assessmentService: AssessmentService,
  ) { }

  ngOnInit() {
    this.initiateForm();
    if (this.data.fetchAllUsers) {
      const start = Date.now();
      this.dialogService.openSpinner();
      const pageSize = 100;
      const concurrency = 3;
      const maxErrorCount = 5;
      const totalPages = Math.max(0, Math.ceil(this.data.userCount / pageSize));
      if (!totalPages) return;
      const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
      const pageMap = new Map<number, any[]>();
      firstValueFrom(
        from(pages).pipe(
          mergeMap(
            (p) => this.userService.getUserList(this.data.userType, ["skills", "agency", "baer_roles.baer_role"], p, pageSize)
              .pipe(
                retry({
                  count: maxErrorCount,
                  delay: (error, retryCount) => {
                    const status = error?.status ?? 0;
                    const retryable = status === 429 || (status >= 500 && status <= 600);
                    if (!retryable) {
                      this.dialogService.closeSpinner();
                      throw error;
                    }
                    const ms = Math.min(30000, 500 * Math.pow(2, retryCount - 1));
                    return timer(ms);
                  }
                }),
                tap((res) => {
                  pageMap.set(p, res.users ?? []);
                })
              ),
            concurrency
          ),
          toArray(),
          map(() => {
            const ordered : any[] = [];
            for (let p = 1; p <= totalPages; p++) {
              ordered.push(...(pageMap.get(p) ?? []));
            }
            return ordered;
          })
        )
      ).then(allUsers => {
        this.data.users = allUsers;
        this.users.set(allUsers);
        this.dialogService.closeSpinner();
      }).catch(err => {
        console.log(err);
        this.dialogService.closeSpinner();
      }).finally(() => {
        const end = Date.now();
        console.log(`It took ${end - start}ms to fetch all users`);
      });
    } else {
      this.users.set(this.data.users);
    }
  }

  private initiateForm() {
    this.formGroup = this.fb.group({
      selectAll: [true],
      name: [true],
      email: [true],
      phone: [true],
      positions: [true],
      specificPositions: [['all', ...this.data.positions.map((opt: { value: any; }) => opt.value)]],
      agency: [true],
      specificAgencies: [['all', ...this.data.agencies.map((opt: { value: any; }) => opt.value)]],
      location: [true],
      specificRegions: [['all', ...this.data.regions]],
      specificForests: [['all', ...this.data.forests]],
      assignmentStatus: [true],
      specificAssignmentStatuses: [['all', ...this.assignmentStatuses.map((opt: { value: any; }) => opt.value)]],
      skills: [true],
      specificSkills: [['all', ...this.data.skills.map((opt: { value: any; }) => opt.value)]],
      vgisCertified: [],
      nisBaerCertified: [],
    });
    this.defaultFormValues = this.formGroup.getRawValue();
    for (const controlName in this.previousSelections) {
      this.previousSelections[controlName] = this.formGroup.controls[controlName].value;
    }
  }

  private updateSelectAllIfSelected(values: { [x: string]: any; }) {
    if (values['selectAll']) {
      if (this.allSelected()) {
        this.indeterminate.set(false);
      } else {
        this.indeterminate.set(true);
      }
    }
  }

  searchRegionChanged(): void {
    this.data.forests = this.formGroup.controls['specificRegions'].value
      .flatMap((region: any) => this.assessmentService.getForestNameList(region ?? undefined));
    this.formGroup.controls['specificForests'].setValue(['all', ...this.data.forests]);
  }

  allSelected() {
    let allSelected = true;
    for (const controlName of this.selectAllControlNames) {
      const currentValue = this.formGroup.controls[controlName].getRawValue();
      if (Array.isArray(currentValue)) {
        if (!currentValue.includes('all')) {
          allSelected = false;
          break;
        }
      } else if (!currentValue) {
        allSelected = false;
        break;
      }
    }
    return allSelected;
  }

  clearForm() {
    for (const controlName in this.formGroup.controls) {
      const currentValue = this.formGroup.controls[controlName].getRawValue();
      if (Array.isArray(currentValue)) {
        this.formGroup.controls[controlName].setValue([]);
      } else {
        this.formGroup.controls[controlName].setValue(false);
      }
    }
  }

  selectAllChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.formGroup.reset(this.defaultFormValues);
      this.showCertOptions.set(true);
    } else {
      this.clearForm();
      this.showCertOptions.set(false);
    }
    this.filterUsers();
  }

  checkboxChange(transient: boolean = false) {
    if (!transient) {
      this.updateSelectAllIfSelected(this.formGroup.value);
    }
    this.filterUsers();
  }

  selectionChange(event: MatSelectChange, controlName: string) {
    switch (controlName) {
      case 'specificSkills':
        this.normalizeSelection(event, controlName, this.data.skills);
        if (this.previousSelections[controlName].includes(4)) {
          this.showCertOptions.set(true);
        } else {
          this.showCertOptions.set(false)
        }
        break;
      case 'specificAgencies':
        this.normalizeSelection(event, controlName, this.data.agencies);
        break;
      case 'specificPositions':
        this.normalizeSelection(event, controlName, this.data.positions);
        break;
      case 'specificRegions':
        this.normalizeSelection(event, controlName, this.data.regions, false);
        this.searchRegionChanged();
        break;
      case 'specificForests':
        this.normalizeSelection(event, controlName, this.data.forests, false);
        break;
      case 'specificAssignmentStatuses':
        this.normalizeSelection(event, controlName, this.assignmentStatuses);
        break;
    }
    this.updateSelectAllIfSelected(this.formGroup.value);
    this.filterUsers();
  }

  private normalizeSelection(event: MatSelectChange, controlName: string, dataSource: any[], isObjectList: boolean = true) {
    console.log(event)
    const currentSelection = event.value as string[];
    const totalOptions = dataSource.map(opt => isObjectList ? opt.value : opt);
    const hasAll = currentSelection.includes('all');
    const withoutAll = currentSelection.filter(opt => opt !== 'all');
    const isAllOtherSelected = withoutAll.length === totalOptions.length;
    const formControl = this.formGroup.controls[controlName];
    const added = currentSelection.filter((opt: any) => !this.previousSelections[controlName].includes(opt));
    const removed = this.previousSelections[controlName].filter(opt => !currentSelection.includes(opt));
    // User selects all -> select everything
    if (added.includes('all')) {
      formControl.patchValue(['all', ...totalOptions], { emitEvent: false });
    }
    // User deselects all -> clear everything
    else if (removed.includes('all')) {
      formControl.patchValue([], { emitEvent: false });
    }
    // All others selected manually -> auto-select all
    else if (!hasAll && isAllOtherSelected) {
      formControl.patchValue(['all', ...totalOptions], { emitEvent: false });
    }
    // All selected but user deselects something -> deselect all
    else if (hasAll && withoutAll.length < totalOptions.length) {
      formControl.patchValue(withoutAll, { emitEvent: false });
    }
    this.previousSelections[controlName] = formControl.value;
  }

  getSelectedTexts(controlName: string, dataSource: any[], isObjectList: boolean = true) {
    if (!isObjectList) return dataSource.filter(opt => this.formGroup.controls[controlName].value?.includes(opt));
    return dataSource.filter(opt => this.formGroup.controls[controlName].value?.includes(opt.value)).map(opt => opt.text);
  }

  filterUsers() {
    this.users.set(this.data.users.filter((user: any) => {
      if (this.formGroup.controls['positions'].value === true && !this.formGroup.controls['specificPositions'].value.includes('all')) {
        if (!user.baer_roles.some((role: { baer_role: { value: any; }; }) => this.formGroup.controls['specificPositions'].value.includes(role.baer_role?.value))) return false;
      }
      if (this.formGroup.controls['agency'].value === true && !this.formGroup.controls['specificAgencies'].value.includes('all')) {
        if (!this.formGroup.controls['specificAgencies'].value.includes(user.agency?.value)) return false;
      }
      if (this.formGroup.controls['location'].value === true) {
        if (!this.formGroup.controls['specificRegions'].value.includes('all')) {
          if (!this.formGroup.controls['specificRegions'].value.includes(user.region)) return false;
        }
        if (!this.formGroup.controls['specificForests'].value.includes('all')) {
          if (!this.formGroup.controls['specificForests'].value.includes(user.forest_name)) return false;
        }
      }
      if (this.formGroup.controls['assignmentStatus'].value === true && !this.formGroup.controls['specificAssignmentStatuses'].value.includes('all')) {
        if (!this.formGroup.controls['specificAssignmentStatuses'].value.includes(user.assignment_status)) return false;
      }
      if (this.formGroup.controls['skills'].value === true && !this.formGroup.controls['specificSkills'].value.includes('all')) {
        if (!user.skills.some((skill: { skill: { value: any; }; }) => this.formGroup.controls['specificSkills'].value.includes(skill.skill?.value))) return false;
        if (this.previousSelections['specificSkills'].includes(4)) {
          if (this.formGroup.controls['vgisCertified'].value) {
            if (!user.VGIS) return false;
          }
          if (this.formGroup.controls['nisBaerCertified'].value) {
            if (!user.NIS_BAER) return false;
          }
        }
      }
      return true;
    }));
  }

  exportToCsv() {
    this.dialogService.openSpinner();
    const selectedColumnControlNames = this.csvColumnControlNames.filter(controlName => this.formGroup.controls[controlName].value === true);
    const rows = this.filteredUsers().map(user => {
      const row: any = {};
      for (const controlName of selectedColumnControlNames) {
        switch (controlName) {
          case 'name':
            row.Name = user.full_name || '--';
            break;
          case 'email':
            row.Email = user.email || '--';
            break;
          case 'phone':
            row['Phone Number'] = user.phone_num || '--';
            break;
          case 'positions':
            row.Positions = user.baer_roles.map((role: { baer_role: { text: any; }; }) => role.baer_role?.text).join(', ');
            break;
          case 'agency':
            row.Agency = user.agency?.text || '--';
            break;
          case 'location':
            row.Region = user.region || '--';
            row.Forest = user.forest_name || '--';
            break;
          case 'assignmentStatus':
            row['Fire Assignment Status'] = this.assignmentStatuses.find(status => status.value === user.assignment_status)?.text || '--';
            break;
          case 'skills':
            row.Skills = user.skills.map((skill: { skill: { text: any; }; }) => skill.skill?.text).join(', ');
            break;
        }
      }
      return row;
    });
    this.downloadCsv(rows);
  }

  downloadCsv(data: any[]): void {
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          let value = row[header];
          if (Array.isArray(value)) {
            value = value.join('; ');
          }
          value ??= '';
          return `"${String(value).replaceAll('"', '""')}"`;
        }).join(',')
      )
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv:charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Roster-${this.getFormattedDate()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    this.dialogService.closeSpinner();
    this.dialogRef.close();
  }

  getFormattedDate() {
    const date = new Date();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return mm + dd + yyyy;
  }
}
